import API from '../../../../services/api-middleware';
import { DownloadFilePayload } from '../../../../utils/interface-types';
import type { AppsFunctions } from '../../all-assistants/all-assistants.props';
import { MCPServerPayload } from '../components/tune-modal/tune-modal.props';
import type { WebSearchMode } from '../lib/chat-model-persist';
import { Session } from '../pages/chat-screen/chat-screen.props';

const baseUrl = process.env.REACT_APP_LLM_SERVER_URL;

// start new chat
export const generateNewChat = async (
  queryString: string | undefined,
  model: string | undefined,
  useFile: string | undefined | boolean,
  fileId?: string,
  type?: string,
  sessionId?: string,
  webSearchEnabled?: boolean,
  codeInterpreterEnabled?: boolean,
  webSearchMode?: WebSearchMode,
) => {
  let token = localStorage.getItem('token');
  if (token !== null) {
    token = JSON.parse(token);
  }
  const payload = {
    question: queryString,
    model,
    file_flag: !!useFile,
    file_id: fileId,
    type,
    service: 'chat',
    web_search: webSearchEnabled === true ? webSearchEnabled : false,
    web_search_mode: webSearchMode ?? (webSearchEnabled === true ? 'on' : 'off'),
    code_interpreter: codeInterpreterEnabled === true ? codeInterpreterEnabled : false,
  };
  try {
    const response = await API(baseUrl).post(`chat/${sessionId}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export type StreamEvent =
  | { type: 'thinking'; delta: string }
  | { type: 'output'; delta: string }
  | {
      type: 'token_usage';
      input_tokens: number;
      reasoning_tokens: number;
      output_tokens: number;
    }
  | { type: 'generated_files'; generated_files: unknown }
  | { type: 'web_search_started'; query?: string }
  | { type: 'web_search_used'; webSearchUsed?: boolean }
  | {
      type: 'sources';
      sources?: { title?: string; link?: string; hostname?: string; snippet?: string }[];
    }
  | { type: 'function_call_started' | 'tool_call_started'; tool_name?: string; id?: string }
  | {
      type: 'function_call_code' | 'tool_call_code' | 'tool_code';
      tool_name?: string;
      call_id?: string;
      delta?: string | object;
      id?: string;
    }
  | { type: 'tool_code_done' | 'tool_completed'; tool_name?: string; id?: string }
  | { type: 'error'; error: string }
  | { type: 'mcp_warning'; message: string }
  | {
      type: 'mcp_connection_failed';
      mcp_id?: number;
      mcp_name?: string;
      message?: string;
    }
  | {
      type: 'mcp_tool_result';
      tool_name?: string;
      tool_id?: string;
      mcp_id?: number;
      error?: unknown;
    }
  | {
      type: 'routing_decision';
      logical_model?: string;
      model_name?: string;
      routing_reason?: string;
      routing_score?: number;
      routing_band?: number;
      resolved_routing_band?: number;
      target_routing_band?: number;
      selection_reason?: string;
      pick_tier?: 'economy' | 'balanced' | 'premium' | string;
    }
  | { type: 'done' }
  | { type: string; [key: string]: unknown }; // fallback

export type GenerateNewChat2StreamOptions = {
  signal?: AbortSignal;
  retry?: boolean;
  routingMode?: 'auto' | 'manual';
  webSearchMode?: WebSearchMode;
};

export const terminateChatStream = async (sessionId: string) => {
  let token = localStorage.getItem('token');
  if (token !== null) {
    token = JSON.parse(token);
  }
  try {
    const response = await API(baseUrl).post(
      `chat/terminate_stream/${sessionId}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const generateNewChat2 = async (
  queryString: string | undefined,
  model: string | undefined,
  useFile: string | undefined | boolean,
  fileId?: string,
  type?: string,
  sessionId?: string,
  webSearchEnabled?: boolean,
  codeInterpreterEnabled?: boolean,
  agentId?: number,
  excelServerAgent?: boolean,
  thinkingEnabled?: boolean,
  streamOptions?: GenerateNewChat2StreamOptions,
  // NEW: callbacks for streaming
  onEvent?: (event: StreamEvent) => void,
  onComplete?: () => void,
  onError?: (error: Error) => void,
) => {
  let token = localStorage.getItem('token');
  if (token !== null) {
    token = JSON.parse(token);
  }

  const payload: {
    question: string | undefined;
    model: string | undefined;
    file_flag: boolean;
    file_id?: string;
    type?: string;
    service: string;
    web_search: boolean;
    web_search_mode: WebSearchMode;
    code_interpreter: boolean;
    thinking?: boolean;
    agent_id?: number;
    excel_server_agent?: boolean;
    retry?: boolean;
    routing_mode?: 'auto' | 'manual';
  } = {
    question: queryString,
    model,
    file_flag: !!useFile,
    file_id: fileId,
    type,
    service: 'chat',
    web_search: webSearchEnabled === true ? webSearchEnabled : false,
    web_search_mode: streamOptions?.webSearchMode ?? (webSearchEnabled === true ? 'on' : 'off'),
    code_interpreter: codeInterpreterEnabled === true ? codeInterpreterEnabled : false,
  };

  // Add thinking parameter if provided
  if (thinkingEnabled !== undefined) {
    payload.thinking = thinkingEnabled;
  }

  // Add agent_id if provided
  if (agentId !== undefined && agentId !== null) {
    payload.agent_id = agentId;
  }

  // Add excel_server_agent parameter when Excel document context is used
  if (excelServerAgent === true) {
    payload.excel_server_agent = true;
  }

  if (streamOptions?.retry === true) {
    payload.retry = true;
  }

  const routingMode = streamOptions?.routingMode;
  if (routingMode === 'auto' || routingMode === 'manual') {
    payload.routing_mode = routingMode;
  }

  try {
    // IMPORTANT: use fetch here, not your Axios API helper
    const response = await fetch(`${baseUrl}/chat/${sessionId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify(payload),
      signal: streamOptions?.signal,
    });

    if (!response.ok) {
      let errorMessage = 'Failed to send message';
      try {
        const errorData = await response.text();
        const parsedError = JSON.parse(errorData);
        errorMessage = parsedError.message || errorMessage;
      } catch (e) {
        errorMessage = `Failed to send message: ${response.status} ${response.statusText}`;
      }
      const err = new Error(errorMessage);
      onError?.(err);
      throw err;
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      const err = new Error('No response body');
      onError?.(err);
      throw err;
    }

    const processStream = async () => {
      let buffer = '';
      let streamEnded = false;

      const dispatchSseLine = (line: string) => {
        if (!line.startsWith('data: ')) return;

        const data = line.slice(6).trim();
        if (!data) return;

        try {
          const parsed = JSON.parse(data);

          switch (parsed.type) {
            case 'thinking':
              if (parsed.delta) {
                onEvent?.({
                  type: 'thinking',
                  delta: parsed.delta,
                });
              }
              break;

            case 'output':
              if (parsed.delta) {
                onEvent?.({
                  type: 'output',
                  delta: parsed.delta,
                });
              }
              break;

            case 'token_usage':
              onEvent?.({
                type: 'token_usage',
                input_tokens: parsed.input_tokens,
                reasoning_tokens: parsed.reasoning_tokens,
                output_tokens: parsed.output_tokens,
              });
              break;

            case 'generated_files':
              onEvent?.({
                type: 'generated_files',
                generated_files: parsed.files || parsed.generated_files,
              });
              break;

            case 'function_call_started':
            case 'tool_call_started':
              onEvent?.({
                type: 'function_call_started',
                tool_name: parsed.tool_name,
                id: parsed.id,
              });
              break;

            case 'function_call_code':
            case 'tool_code':
            case 'tool_call_code':
              onEvent?.({
                type: parsed.type,
                tool_name: parsed.tool_name,
                call_id: parsed.call_id,
                delta: parsed.delta,
                id: parsed.id,
              });
              break;

            case 'tool_code_done':
            case 'tool_completed':
              onEvent?.({
                type: parsed.type,
                tool_name: parsed.tool_name,
                id: parsed.id,
              });
              break;

            case 'error': {
              const errText = parsed.error || 'Unknown error';
              const err = new Error(errText);
              onEvent?.({ type: 'error', error: errText });
              onError?.(err);
              streamEnded = true;
              break;
            }

            case 'done':
              onEvent?.({ type: 'done' });
              onComplete?.();
              streamEnded = true;
              break;

            default:
              onEvent?.(parsed as StreamEvent);
          }
        } catch {
          // ignore partial/invalid JSON chunks
        }
      };

      const flushBuffer = (pending: string) => {
        pending.split('\n').forEach(dispatchSseLine);
      };

      let result = await reader.read();

      while (!result.done && !streamEnded) {
        buffer += decoder.decode(result.value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        lines.forEach(dispatchSseLine);

        if (streamEnded) break;

        if (streamOptions?.signal?.aborted) {
          flushBuffer(buffer);
          buffer = '';
          try {
            reader.cancel();
          } catch {
            // Reader may already be closed after abort.
          }
          return;
        }

        result = await reader.read(); // eslint-disable-line no-await-in-loop
      }

      if (buffer.trim()) {
        flushBuffer(buffer);
      }
    };

    await processStream();

    if (streamOptions?.signal?.aborted) {
      return;
    }

    // If stream ended without explicit "done"
    onComplete?.();
  } catch (error) {
    if (streamOptions?.signal?.aborted) {
      return;
    }
    onError?.(error as Error);
    throw error;
  }
};

// upload file
export const uploadFile = async (
  file: File,
  sessionId: string,
  fileMessage?: string,
  codeInterpreterEnabled?: boolean,
  signal?: AbortSignal,
) => {
  let token = localStorage.getItem('token');
  if (token !== null) {
    token = JSON.parse(token);
  }
  try {
    const formData = {
      file,
      message: fileMessage || '',
      code_interpreter: codeInterpreterEnabled === true ? 'True' : 'False',
    };
    const response = await API(baseUrl).post(
      `upload/${sessionId}
    `,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
        signal,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// Get All Pinned Chat Sessions
export const GetAllPinnedChatSessions = async (payload: { search: string }) => {
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).get(`chat_pinned_session?=&search=${payload.search}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Get All Chat Sessions in Chat History
export const GetAllChatHistorySessions = async (payload: {
  limit: number;
  offset: number;
  search: string;
}) => {
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).get(
      `chat_session?limit=${payload.limit}&offset=${payload.offset}&search=${payload.search}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// Rename a session
export const RenameChatSession = async (sessionId: number, name: string, pinned: number) => {
  try {
    const payload = {
      name,
      pinned: pinned === 1,
    };
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).put(`chat_session/${sessionId}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Delete a session
export const DeleteChatSession = async (sessionId: number) => {
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).delete(`chat_session/${sessionId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Clear All Session
export const ClearAllChatSessions = async () => {
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).delete(`chat/session/clear`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Pin or Unpin a session
export const PinUnpinChatSession = async (session: Session) => {
  try {
    const payload = {
      pinned: session.pinned === 0,
      name: session.name,
    };
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).put(`chat_session/${session.id}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export type PermittedChatModelRow = {
  id: number;
  name: string;
  provider_id?: number;
  provider_name?: string;
  supports_tools?: boolean;
  supports_reasoning?: boolean;
  input_price_per_million?: number;
  output_price_per_million?: number;
  model_status?: string;
};

export const fetchPermittedChatModels = async (): Promise<PermittedChatModelRow[]> => {
  let token = localStorage.getItem('token');
  if (token !== null) {
    token = JSON.parse(token);
  }
  const response = await API(baseUrl).get('chat/permitted-models', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const models = response?.data?.data?.models;
  return Array.isArray(models) ? models : [];
};

// Creatting New Session
export const CreateNewChatSession = async (data: {
  provider_id?: number;
  model_id?: number;
  routing_mode?: 'auto' | 'manual';
}) => {
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).post(`chat_session`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update model for provider
export const UpdateModelForProvider = async (data: {
  sessionId: string;
  model: string;
  type: string;
}) => {
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).post(
      `chat/model/${data?.sessionId}`,
      { model: data?.model, type: data?.type },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// Get Session By Id
export const GetSessionById = async (sessionId: string | number) => {
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).get(`chat_session/${sessionId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Create a MCP chat service
export const CreateMcpChatService = async (data: MCPServerPayload) => {
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).post(`chat/mcp`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Get all MCP chat services
export const GetAllMcpChatService = async () => {
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).get(`chat/mcp`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Delete a MCP chat service
export const DeleteMcpChat = async (mcpId: number) => {
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).delete(`chat/mcp/${mcpId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

const userServerUrl = process.env.REACT_APP_USER_SERVER_URL;

export const DiscoverMcpService = async (url: string, transportType: string) => {
  const response = await API(baseUrl).get(
    `chat/mcp/discover?url=${encodeURIComponent(url)}&transportType=${encodeURIComponent(transportType)}`,
  );
  return response;
};

export const RefreshMcpToolsService = async (mcpId: number) => {
  const response = await API(baseUrl).post(`chat/mcp/${mcpId}/tools/refresh`, null);
  return response;
};

export const CallMcpToolService = async (
  mcpId: number,
  name: string,
  args: Record<string, unknown>,
) => {
  const response = await API(userServerUrl).post(`chat/mcp/${mcpId}/tools/call`, {
    name,
    arguments: args,
  });
  return response;
};

export const ReadMcpResourceService = async (mcpId: number, uri: string, signal?: AbortSignal) => {
  const response = await API(userServerUrl).get(
    `chat/mcp/${mcpId}/resources/read?uri=${encodeURIComponent(uri)}`,
    { signal },
  );
  return response;
};

// Edit Mcp chat service
export const EditMcpChatServce = async (
  data: { status?: string; disabled_tools?: string[] },
  mcpId: number,
) => {
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).post(`/chat/mcp/${mcpId}`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const downloadFileInChat = async (payload: DownloadFilePayload) => {
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).get(
      `api/download-file?container_id=${payload.container_id}&file_id=${payload.file_id}&filename=${payload.filename}&session_id=${payload.session_id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAllKnowledgeBaseFolders = async () => {
  let token = localStorage.getItem('token');
  if (token !== null) {
    token = JSON.parse(token);
  }
  try {
    const services = ['system', 'G-drive', 'aws'];
    const baseParams = 'limit=100&offset=0&search=&filters=[{"type":"created_at","value":[]}]';
    const requests: Promise<{ data: { data: { folders: { id: number; name: string }[] } } }>[] = [];
    services.forEach((service) => {
      requests.push(
        API(baseUrl)
          .get(`knowledge-base?${baseParams}&service=${service}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          .then((res) => {
            return res;
          })
          .catch(() => {
            return { data: { data: { folders: [] } } };
          }),
      );
    });

    services.forEach((service) => {
      requests.push(
        API(baseUrl)
          .get(`knowledge-base/public?${baseParams}&service=${service}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          .then((res) => {
            return res;
          })
          .catch(() => {
            return { data: { data: { folders: [] } } };
          }),
      );
    });

    const responses = await Promise.all(requests);

    // Combine all KB data from all responses
    const allFolders = responses.flatMap((res) => res.data?.data?.folders || []);

    // Categorize based on is_public field
    const allKB = allFolders.map((kb: { id: number; name: string; is_public?: number }) => ({
      ...kb,
      type: kb.is_public === 1 ? 'public' : 'private',
    }));

    return { data: { data: allKB }, status: 200 };
  } catch (error) {
    throw error;
  }
};

// TO create a chat agent
export const createChatAgent = async (data: {
  agent_name: string;
  description?: string;
  provider_id: number;
  model_id: number;
  system_prompt: string;
  temperature?: number;
  max_tokens?: number;
  knowledge_base_enabled?: boolean;
  knowledge_base_id?: number;
  code_interpreter_enabled?: boolean;
  mcp_enabled?: boolean;
  mcp_id?: number | number[];
  web_search_enabled?: boolean;
}) => {
  let token = localStorage.getItem('token');
  if (token !== null) {
    token = JSON.parse(token);
  }
  try {
    const response = await API(baseUrl).post('agent', data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// my agents (same query-param pattern as assistant list)
export const getMyAgents = async (payload: AppsFunctions) => {
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const encodedFilters = encodeURIComponent(JSON.stringify(payload.filters));
    const response = await API(baseUrl).get(
      `agents?search=${payload.search}&limit=${payload.limit}&offset=${payload.offset}&filters=${encodedFilters}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// my and public agents (same query-param pattern as assistant list)
export const getAllAgents = async () => {
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const response = await API(baseUrl).get(`agents/all`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Public agents list (same pattern as assistant/public)
export const getPublicAgents = async (payload: AppsFunctions) => {
  try {
    let token = localStorage.getItem('token');
    if (token !== null) {
      token = JSON.parse(token);
    }
    const encodedFilters = encodeURIComponent(JSON.stringify(payload.filters));
    const response = await API(baseUrl).get(
      `agents/public?search=${payload.search}&limit=${payload.limit}&offset=${payload.offset}&filters=${encodedFilters}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// Get agent by ID
export const getAgentById = async (agentId: number) => {
  let token = localStorage.getItem('token');
  if (token !== null) {
    token = JSON.parse(token);
  }
  try {
    const response = await API(baseUrl).get(`agent/${agentId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Make agent public (same pattern as assistant/public)
export const makeAgentPublic = async (agentId: number) => {
  let token = localStorage.getItem('token');
  if (token !== null) {
    token = JSON.parse(token);
  }
  try {
    const response = await API(baseUrl).post(
      `agents/public`,
      { id: agentId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// Delete an agent
export const deleteAgent = async (agentId: number) => {
  let token = localStorage.getItem('token');
  if (token !== null) {
    token = JSON.parse(token);
  }
  try {
    const response = await API(baseUrl).delete(`agent/${agentId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Update an agent
export const updateAgent = async (
  agentId: number,
  data: {
    agent_name?: string;
    description?: string;
    provider_id?: number;
    model_id?: number;
    system_prompt?: string;
    temperature?: number;
    max_tokens?: number;
    knowledge_base_enabled?: boolean;
    knowledge_base_id?: number;
    code_interpreter_enabled?: boolean;
    mcp_enabled?: boolean;
    mcp_id?: number | number[];
    web_search_enabled?: boolean;
  },
) => {
  let token = localStorage.getItem('token');
  if (token !== null) {
    token = JSON.parse(token);
  }
  try {
    const response = await API(baseUrl).put(`agent/${agentId}`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export { patchToolCallResult } from '../lib/mcp-tool-utils';
